/**
 * settings.ts —— 局中设置 / 偏好（localStorage 持久化）
 *
 * 与开局设置（GameConfig，属引擎）分开：这里只放**不影响牌局逻辑**的偏好项，
 * 因此可随时改而不破坏「同种子可复现」（AC3.3 / AC4.10）。
 */

import { createStore, type Store } from './store';

export type Speed = 1 | 2 | 4;
export type MotionPref = 'system' | 'on' | 'off';
export type EquityMode = 'live' | 'review';
/**
 * 决策评分语气模式（需求二 FR2-2 · 呈现层）。
 *   - `beginner` 新手鼓励：评级措辞中性 / 鼓励化，无高饱和 error 红字、无「严重失误」字样；
 *   - `pro`      专业直述：保留量化直评（最优 / 可接受 / 失误 / 严重失误 + 少赢 X% 底池 + 红色语义）。
 * 只影响评级措辞 + 配色 + 详解语气，**不影响判定档位与 EV 差数值**（AC2-6）。首次用户默认新手鼓励。
 */
export type ScoreToneMode = 'beginner' | 'pro';
/** R13-2：弃牌后推进模式。turbo = 加速打完本手剩余 AI 行动；normal = 原速（现状）。 */
export type FoldAdvanceMode = 'turbo' | 'normal';

export interface Settings {
  /** 牌局速度，影响发牌 / 动画 / AI 思考停顿（PRD 模块 3）。 */
  readonly speed: Speed;
  readonly fourColor: boolean;
  readonly motion: MotionPref;
  /** 全下与 >50% 筹码加注二次确认（PRD 7.6，可关闭）。 */
  readonly allInConfirm: boolean;
  /** 赢率计算器总开关，默认关闭（AC4.1）。 */
  readonly equityEnabled: boolean;
  readonly equityMode: EquityMode;
  /** 赢率面板折叠状态（lg 右下浮层，跨手牌记忆）。 */
  readonly equityCollapsed: boolean;
  /**
   * 窄断点（md 横向条 / sm 底部抽屉）的折叠状态，跨手牌记忆。
   * 默认折叠：设计方案要求 md 常态是 56 高单行赢率条、sm 常态只留 44 高把手，
   * 「行动优先于信息」——详情由用户主动展开。
   */
  readonly equityCollapsedNarrow: boolean;
  /** 决策评分开关（PRD 5.1，可关闭）。 */
  readonly scoreEnabled: boolean;
  /** 决策评分语气模式（需求二 FR2-2）：新手鼓励 / 专业直述，持久化、跨手跨会话保持。 */
  readonly scoreToneMode: ScoreToneMode;
  /** 快捷下注档位（占「跟注后底池」比例），可自定义并持久化（AC5.1）。 */
  readonly quickBets: readonly number[];
  /** 行动时限：0 = 关闭 / 15 / 30 秒。 */
  readonly actionClock: 0 | 15 | 30;
  /** 复盘时是否展示弃牌者底牌。 */
  readonly revealFolded: boolean;
  /**
   * 结算后是否自动进入下一手（R1，第 9 轮）。默认 false = 手动推进：
   * 结算浮层停留，需玩家点「继续 / 下一手」或按 Enter/空格 才进下一手。
   * 开启后仅**非摊牌手**自动推进（默认停留 ≥5s，倍速不缩短此窗口）；
   * 摊牌手一律等待玩家主动继续（要同时读净额 + 主/边池归属 + 亮牌）。
   */
  readonly autoContinue: boolean;
  /**
   * R13-2：弃牌后推进模式的记忆值（仅在「每次询问」关闭时直接生效）。默认 'normal'（原速）。
   * turbo 只压缩「本手剩余 AI 行动」的时延，是本手瞬时态，不写 speed、不改结算。
   */
  readonly foldAdvanceMode: FoldAdvanceMode;
  /**
   * R13-2：弃牌后是否每次都弹出「加速 / 原速」二选一。默认 true（每次询问）。
   * 勾选二选一里的「记住」会把它置 false，并把 foldAdvanceMode 写成本次选择；
   * 设置面板可改回模式并重新开启「每次询问」。
   */
  readonly foldAdvanceAsk: boolean;
  /** 调试：人为拖慢赢率计算，用于验证加载态 / 慢速提示态（不改变结果口径）。 */
  readonly debugSlowEquity: boolean;
}

export const DEFAULT_SETTINGS: Settings = {
  speed: 1,
  fourColor: false,
  motion: 'system',
  allInConfirm: true,
  equityEnabled: false,
  equityMode: 'live',
  equityCollapsed: false,
  equityCollapsedNarrow: true,
  scoreEnabled: true,
  scoreToneMode: 'beginner',
  quickBets: [0.5, 0.75, 1, 2],
  actionClock: 0,
  revealFolded: false,
  autoContinue: false,
  foldAdvanceMode: 'normal',
  foldAdvanceAsk: true,
  debugSlowEquity: false,
};

const KEY = 'riverside.settings.v1';

function read(): Settings {
  if (typeof localStorage === 'undefined') return DEFAULT_SETTINGS;
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return DEFAULT_SETTINGS;
    const parsed = JSON.parse(raw) as Partial<Settings>;
    return normalizeSettings({ ...DEFAULT_SETTINGS, ...parsed });
  } catch {
    return DEFAULT_SETTINGS;
  }
}

/** 收敛非法持久化值，避免坏档把 UI 打挂。 */
export function normalizeSettings(s: Settings): Settings {
  const speed: Speed = s.speed === 2 || s.speed === 4 ? s.speed : 1;
  const clock = s.actionClock === 15 || s.actionClock === 30 ? s.actionClock : 0;
  const quick = Array.isArray(s.quickBets)
    ? s.quickBets.filter((n) => typeof n === 'number' && n >= 0.25 && n <= 3).slice(0, 5)
    : DEFAULT_SETTINGS.quickBets;
  return {
    ...s,
    speed,
    actionClock: clock,
    quickBets: quick.length ? quick : DEFAULT_SETTINGS.quickBets,
    motion: s.motion === 'on' || s.motion === 'off' ? s.motion : 'system',
    equityMode: s.equityMode === 'review' ? 'review' : 'live',
    scoreToneMode: s.scoreToneMode === 'pro' ? 'pro' : 'beginner',
    foldAdvanceMode: s.foldAdvanceMode === 'turbo' ? 'turbo' : 'normal',
    foldAdvanceAsk: s.foldAdvanceAsk === false ? false : true,
  };
}

export const settingsStore: Store<Settings> = createStore<Settings>(read());

settingsStore.subscribe(() => {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(KEY, JSON.stringify(settingsStore.get()));
  } catch {
    /* 容量不足或隐私模式：偏好丢失不影响牌局，静默忽略 */
  }
});

export function patchSettings(patch: Partial<Settings>): void {
  settingsStore.set((prev) => normalizeSettings({ ...prev, ...patch }));
}

/** 把偏好映射到 <body> 上的 data 属性与 CSS 变量（四色牌 / 动效 / 倍速）。 */
export function applySettingsToDom(s: Settings): void {
  if (typeof document === 'undefined') return;
  const b = document.body;
  b.dataset.fourColor = s.fourColor ? 'on' : 'off';
  if (s.motion === 'system') delete b.dataset.motion;
  else b.dataset.motion = s.motion;
  b.style.setProperty('--speed', String(s.speed));
}

/** 是否应关闭动效（含系统 prefers-reduced-motion）。 */
export function motionDisabled(s: Settings): boolean {
  if (s.motion === 'off') return true;
  if (s.motion === 'on') return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
